import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { shallow } from 'zustand/shallow';
import { Dialog } from '@components/dialog/Dialog';
import { Button } from '@components/button';
import { ToastType } from '@components/toast';
import { Link } from 'react-router-dom';
import { formatNamedInsuredPolicyNumber, usePatchNamedInsuredUpdateRequest } from '@services/customer';
import type { CustomerNamedInsuredUpdateRequest } from '@services/customer';
import { getFormattedPolicyNumber } from '@utils/policy';
import { useStore } from '@store/GlobalStore';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';

interface Props {
  open: boolean;
  onClose: () => void;
  updateRequest: CustomerNamedInsuredUpdateRequest;
  title?: string;
  header?: ReactNode;
}

export const NamedInsuredTransactionsModal = ({ open, onClose, updateRequest, title, header }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [userAuth] = useStore((state) => [state.userAuth], shallow);
  const { patchUpdateRequest, isPending: isClearing } = usePatchNamedInsuredUpdateRequest();
  const [visibleTransactionCount, setVisibleTransactionCount] = useState(100);

  const canClearFlag = userAuth.isPortalAdmin || userAuth.isAdmin;

  const transactions = updateRequest.endorsementTransactions.slice(0, visibleTransactionCount);
  const hasMoreTransactions = visibleTransactionCount < updateRequest.endorsementTransactions.length;
  const failedPolicies = updateRequest.failedPolicies ?? [];
  const failedPolicyIds = updateRequest.failedPolicyIds ?? [];
  const persistedFlagCleared = Boolean(updateRequest.needsManualEndorsementFlagClearedByUserId);
  const [flagCleared, setFlagCleared] = useState(persistedFlagCleared);
  const showClearButton = canClearFlag && updateRequest.needsManualEndorsement && !flagCleared;

  const allFailedPolicies =
    failedPolicies.length > 0
      ? failedPolicies.map((p) => ({
          id: p.id,
          label:
            p.policyNumber ??
            getFormattedPolicyNumber(p.policyNumberPrefix, p.policyNumberRecord?.policyNumber ?? p.id),
        }))
      : failedPolicyIds.map((id) => ({ id, label: id }));
  const manualSupportCompleted = flagCleared;

  useEffect(() => {
    setFlagCleared(persistedFlagCleared);
  }, [persistedFlagCleared, updateRequest.id]);

  const handleClearFlag = async () => {
    try {
      await patchUpdateRequest({ customerId: updateRequest.customerId, needsManualEndorsement: false });
      setFlagCleared(true);
      enqueueSnackbar('Manual support flag cleared.', { variant: ToastType.Success });
    } catch (error) {
      const message = await getApiErrorMessage(error, 'Failed to clear flag. Please try again.');
      enqueueSnackbar(message, { variant: ToastType.Error });
    }
  };

  const rowSx = (isLast: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    ...(!isLast && { borderBottom: '1px solid', borderColor: 'divider' }),
  });

  const clearFlagFooter = showClearButton ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 3,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.default',
      }}
    >
      <Typography variant='caption' color='text.secondary' sx={{ maxWidth: 480 }}>
        Once you have resolved the policies listed above, click <strong>Mark as Handled</strong> to clear the support
        flag and allow this named insured update to be completed.
      </Typography>
      <Button
        variant='outlined'
        onClick={() => void handleClearFlag()}
        disabled={isClearing}
        isLoading={isClearing}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Mark as Handled
      </Button>
    </Box>
  ) : null;

  return (
    <Dialog
      open={open}
      title={title ?? 'Named Insured Endorsements'}
      handleCloseDialog={onClose}
      size='medium'
      primaryBtnText='Close'
      primaryBtnAction={onClose}
      sxProps={{ paperSx: { maxHeight: '90vh' } }}
      footerContent={clearFlagFooter}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2 }}>
        {header}
        <Typography variant='body1' color='text.main' fontWeight={400}>
          The following endorsements must be bound before this named insured update can be considered complete.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            {updateRequest.oldName}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            →
          </Typography>
          <Typography variant='body2' fontWeight={500}>
            {updateRequest.newName}
          </Typography>
        </Box>

        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {transactions.length === 0 ? (
            <Typography variant='body2' sx={{ py: 1.5 }}>
              No pending endorsement transactions.
            </Typography>
          ) : (
            <>
              {transactions.map((tx, index) => (
                <Box key={tx.id} sx={rowSx(index === transactions.length - 1 && allFailedPolicies.length === 0)}>
                  <Typography variant='body1' color='text.main' sx={{ width: 124, flexShrink: 0 }}>
                    {formatNamedInsuredPolicyNumber(tx)}
                  </Typography>

                  {tx.quoteId && (
                    <Link
                      to={`/quote/${tx.quoteId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={onClose}
                      style={{ textDecoration: 'none' }}
                    >
                      <Button variant='text' sx={{ whiteSpace: 'nowrap', minWidth: 0 }}>
                        Go to endorsement
                      </Button>
                    </Link>
                  )}
                </Box>
              ))}

              {hasMoreTransactions && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                  <Button variant='text' onClick={() => setVisibleTransactionCount((count) => count + 100)}>
                    Load more
                  </Button>
                </Box>
              )}
            </>
          )}

          {allFailedPolicies.length > 0 && (
            <>
              <Alert severity={manualSupportCompleted ? 'info' : 'error'}>
                <AlertTitle>Support Required</AlertTitle>
                {manualSupportCompleted ? (
                  <>Support intervention was required but an admin has marked these policies as completed.</>
                ) : (
                  <>
                    The following policies failed to auto-generate endorsements. Please contact support to complete this
                    named insured change.
                  </>
                )}
              </Alert>
              {allFailedPolicies.map((p, index) => (
                <Box key={p.id} sx={rowSx(index === allFailedPolicies.length - 1)}>
                  <Typography
                    variant='body1'
                    color={manualSupportCompleted ? 'text.main' : 'error.main'}
                    sx={{ width: 124, flexShrink: 0 }}
                  >
                    {p.label}
                  </Typography>
                  <Link
                    to={`/policy/${p.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={onClose}
                    style={{ textDecoration: 'none' }}
                  >
                    <Button variant='text' sx={{ whiteSpace: 'nowrap', minWidth: 0 }}>
                      Go to policy
                    </Button>
                  </Link>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
